'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { CartBadge } from '@eliadmin/core/src/components/header/cart-badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCartQueryOpts } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import { useEffect, useRef } from 'react';
import { Cross1Icon } from '@radix-ui/react-icons';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { usePathname } from 'next/navigation';
import { BiDotsHorizontal } from 'react-icons/bi';

export type HeaderCartButtonProps = {
  className?: {
    root?: string;
    info?: string;
    title?: string;
    price?: string;
    badge?: string;
  };
};

export function HeaderCartButton({ className }: HeaderCartButtonProps) {
  const systemLabels = useSystemLabels();
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const { isOpen, setIsOpen } = useDisclosure();
  const pathname = usePathname();

  useEffect(() => {
    setIsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const timerRef = useRef<number | undefined>(undefined);

  const handleMouseOver = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timerRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, 100) as unknown as number; // Type casting to number
  };

  const { data: cart } = useQuery(useCartQueryOpts());

  return (
    <div className="relative">
      <Link
        href={ROUTES.CART() ?? '#'}
        prefetch={false}
        className="relative"
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
      >
        <Button
          asChild
          variant="outline"
          className={cn(
            'w-fit text-center',
            'relative flex items-center gap-2 rounded-lg p-2 hover:bg-secondary',
            'ea-cart-button',
            className?.root,
          )}
        >
          <div data-ea-visual-tag="button">
            <div className="relative flex h-5 w-5 items-center gap-1">
              <span className="sr-only">{systemLabels('cart')} </span>
              {HEADER_CONFIG.headerMain.cart.icon}

              <CartBadge
                className={cn(
                  'h-5 w-5 bg-destructive-foreground font-light hover:bg-destructive-foreground',
                  className?.badge,
                )}
                variant={HEADER_CONFIG.headerMain.cart.badgeVariant}
              />
            </div>

            {!!templateSettings.header?.cart_text?.enabled && (
              <div className={cn('hidden sm:grid', 'ea-cart-button__info', className?.info)}>
                {/* <p className={cn('mb-1 leading-4', 'ea-cart-button__title', className?.title)}>
              {t('text163')}
            </p> */}
                <p
                  className={cn(
                    'leading-2 min-w-[40px] pr-2 text-end text-sm font-semibold text-muted-foreground',
                    'ea-cart-button__price',
                    className?.price,
                  )}
                >
                  {formatPrice(cart?.items_total || 0, currency, currencySettings)}
                </p>
              </div>
            )}
          </div>
        </Button>
      </Link>
      {isOpen && (
        <div
          onMouseOver={handleMouseOver}
          onMouseLeave={handleMouseLeave}
          className="absolute left-[-182px] top-12 z-50 min-h-40 w-72 rounded-sm border border-border bg-background p-2"
        >
          {!cart?.items?.length && (
            <div className="flex h-40 w-full flex-col items-center justify-center gap-2">
              <Cross1Icon className="h-16 w-16 text-destructive-foreground" />
              <h1 className="font-semibold">Žiadne produkty v košíku</h1>
            </div>
          )}
          {!!cart?.items?.length && (
            <div className="flex min-h-40 flex-col justify-between">
              <div className="hide-scroll-bar flex max-h-[260px] flex-col overflow-y-scroll">
                {cart?.items?.map((item, index) => {
                  return (
                    <Link
                      key={`cart-item-${item.id ?? item.slug ?? item.name ?? index}`}
                      href={item.slug}
                      className="flex items-center gap-2 rounded-sm p-2 hover:bg-[hsl(var(--primary)/0.2)]"
                    >
                      <Image
                        src={item.image.url}
                        alt={resolveImageAlt(item?.image, item?.name)}
                        width={64}
                        height={64}
                        className="max-h-16 max-w-16"
                      />
                      <div className="flex flex-col gap-1 font-semibold">
                        <p className="leading-2 line-clamp-2 text-sm">{item.name}</p>
                        <p className="text-sm text-muted-foreground ">
                          {item.qty} x {item.price}
                          {currency}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
              <div className="flex px-2 pb-2 pt-1">
                <p className="flex-1 whitespace-nowrap text-sm font-bold">
                  Medzisúčet: {formatPrice(cart?.items_total || 0, currency, currencySettings)}
                </p>
              </div>

              <Button asChild>
                <Link href={ROUTES.CART() ?? '#'} prefetch={false}>
                  Prejsť do košíka
                </Link>
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
